// Announcement bar processor module
// Processes color snippets in announcement bar: [[Green]] and {{color}}text{{/color}}

export function initAnnouncementProcessor() {
  // Try to find announcement bar immediately
  let announcementBar = findAnnouncementBar();
  
  if (announcementBar) {
    processAnnouncementBar(announcementBar);
  } else {
    // If not found, use MutationObserver to watch for it being added
    const observer = new MutationObserver((mutations) => {
      const bar = findAnnouncementBar();
      if (bar) {
        processAnnouncementBar(bar);
        observer.disconnect();
      }
    });

    // Observe body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also check after a delay in case it's added asynchronously
    setTimeout(() => {
      const bar = findAnnouncementBar();
      if (bar) {
        processAnnouncementBar(bar);
        observer.disconnect();
      }
    }, 500);
  }
}

function findAnnouncementBar() {
  // Try common Ghost announcement bar selectors
  let announcementBar = document.querySelector('.gh-announcement, .gh-announcement-bar, [data-announcement], .announcement-bar, .gh-site-announcement');
  
  // Exclude if found inside .gh-content (post content)
  if (announcementBar && announcementBar.closest('.gh-content')) {
    announcementBar = null;
  }
  
  if (!announcementBar) {
    // Check body direct children (Ghost sometimes adds it there)
    // But exclude .gh-content and its children
    const contentElement = document.querySelector('.gh-content');
    const bodyChildren = Array.from(document.body.children);
    for (const child of bodyChildren) {
      // Skip if it's the content element or contains it
      if (child === contentElement || child.contains(contentElement)) {
        continue;
      }
      const text = child.textContent || '';
      if (text.includes('[[') || text.includes('{{color')) {
        return child;
      }
    }
  }

  if (!announcementBar) {
    // Check if there's a div right after body or in main
    // But exclude .gh-content and its children
    const contentElement = document.querySelector('.gh-content');
    const possibleBars = document.querySelectorAll('body > div, main > div, .container-global > div');
    for (const bar of possibleBars) {
      // Skip if it's the content element or contains it, or is inside .gh-content
      if (bar === contentElement || bar.contains(contentElement) || bar.closest('.gh-content')) {
        continue;
      }
      const text = bar.textContent || '';
      if (text.includes('[[') || text.includes('{{color')) {
        return bar;
      }
    }
  }

  return announcementBar;
}

function processAnnouncementBar(bar) {
  if (!bar) return;

  // Safety check: never process .gh-content or elements inside it
  const contentElement = document.querySelector('.gh-content');
  if (bar === contentElement || bar.contains(contentElement) || bar.closest('.gh-content')) {
    console.warn('[Announcement Processor] Attempted to process .gh-content as announcement bar. Skipping.');
    return;
  }

  // Find the close button so we never touch it (preserves its event listeners)
  const closeButton = bar.querySelector('button, [data-dismiss], .gh-announcement-bar-close');

  // Find the content element: a direct child that is NOT the close button
  // Fall back to the bar itself if no separate content element exists
  let contentEl = null;
  for (const child of bar.children) {
    if (child !== closeButton && !child.contains(closeButton)) {
      contentEl = child;
      break;
    }
  }
  // If bar has no child content element, work directly on bar but skip the close button
  if (!contentEl) contentEl = bar;

  let html = contentEl.innerHTML || '';
  const originalText = contentEl.textContent || '';

  if (!originalText && !html) return;

  // Color mapping
  const colorMap = {
    'green': {
      hex: '#AAFBB2',
      rgb: '170, 251, 178',
      background: 'linear-gradient(0deg, rgba(170, 251, 178, 0.10) 0%, rgba(170, 251, 178, 0.10) 100%), #1E1E21'
    },
    'yellow': {
      hex: '#F8D72B',
      rgb: '248, 215, 43',
      background: 'linear-gradient(0deg, rgba(248, 215, 43, 0.10) 0%, rgba(248, 215, 43, 0.10) 100%), #1E1E21'
    }
    // Can add more colors here in the future: red, blue, etc.
  };

  // Find color tag like [[Green]] in HTML
  const colorTagRegex = /\[\[([^\]]+)\]\]/;
  const colorMatch = html.match(colorTagRegex) || originalText.match(colorTagRegex);

  let selectedColor = null;

  if (colorMatch) {
    const colorName = colorMatch[1].trim().toLowerCase();
    if (colorMap[colorName]) {
      selectedColor = colorMap[colorName];
      html = html.replace(colorTagRegex, '');
      bar.style.background = selectedColor.background;
    }
  }

  // Set default text color to white
  bar.style.color = '#FFFFFF';

  // Process {{color}}text{{/color}} snippets
  if (selectedColor) {
    const colorSnippetRegex = /\{\{color\}\}([\s\S]*?)\{\{\/?color\}\}/g;
    const matches = [...html.matchAll(colorSnippetRegex)];

    if (matches.length > 0) {
      matches.forEach(match => {
        const styledText = `<span style="color: ${selectedColor.hex}; font-size: 12px; font-style: normal; font-weight: 500; line-height: 18px; letter-spacing: 0.17px;">${match[1]}</span>`;
        html = html.replace(match[0], styledText);
      });
    }
  }

  // Only update innerHTML on the content element (never on the whole bar),
  // so the close button's event listeners are untouched.
  if (html !== contentEl.innerHTML) {
    contentEl.innerHTML = html;
  }

  // Apply text styles to spans and paragraphs inside the content element only
  contentEl.querySelectorAll('span').forEach(span => {
    const c = span.style.color;
    const isWhiteOrUnset = !c || c === 'rgb(255, 255, 255)' || c === '#ffffff' || c === '#FFFFFF';
    if (isWhiteOrUnset) {
      span.style.fontSize = '12px';
      span.style.fontStyle = 'normal';
      span.style.fontWeight = '500';
      span.style.lineHeight = '18px';
      span.style.letterSpacing = '0.17px';
    } else {
      if (!span.style.fontSize) span.style.fontSize = '12px';
      if (!span.style.fontStyle) span.style.fontStyle = 'normal';
      if (!span.style.fontWeight) span.style.fontWeight = '500';
      if (!span.style.lineHeight) span.style.lineHeight = '18px';
      if (!span.style.letterSpacing) span.style.letterSpacing = '0.17px';
    }
  });

  contentEl.querySelectorAll('p').forEach(p => {
    if (p.closest('.gh-content')) return;
    p.style.fontSize = '12px';
    p.style.fontStyle = 'normal';
    p.style.fontWeight = '500';
    p.style.lineHeight = '18px';
    p.style.letterSpacing = '0.17px';
    p.style.color = '#FFFFFF';
    p.style.margin = '0';
  });
}

